import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import { GridContext } from '@bingads-webui-grid/grid-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import {
  Button,
  Input,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import {
  SearchRegular,
} from '@fluentui/react-icons';
import {
  Stack,
} from '@fluentui/react/lib/Stack';
import { CountrySelector } from '../common/components/country/country-selector';
import { StatusSelector } from './status-selector';

const useStyles = makeStyles({
  search: {
    width: '300px',
  },
});

const debouncedEditableFilterDispatch = _.debounce((currentEditableFilterPayload, dispatch) => dispatch({ type: 'SET_EDITABLE_FILTER', payload: currentEditableFilterPayload }), 300);

export const Toolbar = ({ onClickCreate }) => {
  const styles = useStyles();
  const toolbarTokens = {
    childrenGap: 12,
  };

  const i18n = useContext(I18nContext);
  const createButtonLabel = i18n.getString(_TL_('Create'));
  const searchBoxPlaceholder = i18n.getString(_TL_('Search by experiment name'));
  const searchBoxLabel = i18n.getString(_TL_('Experiment name search'));

  const { dispatch } = useContext(GridContext);

  const [countryFilters, setCountryFilters] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);

  const onChangeCountries = useCallback((countries) => {
    setClarityTag({
      IsSelectedLiftExperimentCountryFilter: true,
    });
    setCountryFilters([...countries]);
  }, []);

  const onChangeSearchInput = useCallback((_e, { value }) => {
    setClarityTag({
      IsChangedLiftExperimentSearchFilter: true,
    });
    setSearchFilter(value);
  }, []);

  const onChangeStatuses = useCallback((statuses) => {
    setClarityTag({
      IsSelectedLiftExperimentStatusFilter: true,
    });
    setStatusFilters([...statuses]);
  }, []);

  const fetchExperimentsFilter = useMemo(() => {
    const formattedCountryFilters = countryFilters.map(country => `'${country}'`).join(', ');
    const formattedStatusFilters = statusFilters.map(country => `'${country}'`).join(', ');
    return [
      searchFilter.length ? `contains(tolower(Name), '${searchFilter.toLowerCase()}')` : null,
      countryFilters.length ? `Country in [${formattedCountryFilters}]` : null,
      statusFilters.length ? `Status in [${formattedStatusFilters}]` : null,
    ].filter(fragment => fragment).join(' and ');
  }, [countryFilters, searchFilter, statusFilters]);

  useEffect(() => {
    debouncedEditableFilterDispatch(fetchExperimentsFilter, dispatch);
  }, [fetchExperimentsFilter, dispatch]);

  return (
    <Stack horizontal horizontalAlign="space-between">
      <Stack horizontal tokens={toolbarTokens}>
        <Button className="create-experiment" appearance="primary" onClick={onClickCreate}>{createButtonLabel}</Button>
        <StatusSelector
          onSelect={onChangeStatuses}
          selectedStatuses={statusFilters}
        />
        <CountrySelector
          onSelect={onChangeCountries}
          selectedCountries={countryFilters}
        />
      </Stack>
      <Input
        className={mergeClasses('search-filter', styles.search)}
        aria-label={searchBoxLabel}
        contentBefore={<SearchRegular />}
        onChange={onChangeSearchInput}
        placeholder={searchBoxPlaceholder}
        value={searchFilter}
      />
    </Stack>
  );
};

Toolbar.propTypes = {
  onClickCreate: PropTypes.func.isRequired,
};
